import React, { useRef, useState } from "react";
import { Error, File, InProgress, Tick } from "../../../assets/icons";

interface FileUploadProps {
    accept?: string;
    onFileChange: (file: File | null) => void;
    onClickDownload?: () => void;
    onClickConfirm?: () => void;
    file?: any;
    setFile?: any;
    error?: any;
    setError?: any;
    maxSizeMB?: number;
}

const FileUpload: React.FC<FileUploadProps> = ({
    accept = "",
    onFileChange,
    onClickDownload,
    onClickConfirm,
    file,
    setFile,
    maxSizeMB = 5,
    error,
    setError,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const validateAndHandleFile = (file: File | null) => {
        setFile((prev: any) => ({
            ...prev,
            isSuccess: false,
            uploadProgress: 0,
        }));

        if (!file) {
            setFile((prev: any) => ({
                ...prev,
                fileName: "",
                fileSize: 0,
            }));
            onFileChange(null);
            return;
        }

        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const acceptedExtensions = accept
            .split(',')
            .map(ext => ext.trim().replace('.', '').toLowerCase());

        if (fileExtension && !acceptedExtensions.includes(fileExtension)) {
            setFile((prev: any) => ({
                ...prev,
                fileName: "",
                fileSize: 0,
            }));
            setError((prev: any) => ({
                ...prev,
                error: ["Invalid file type."],
                isValid: false,
            }));
            onFileChange(null);
            return;
        }

        if (file.size > maxSizeMB * 1024 * 1024) {
            setFile((prev: any) => ({
                ...prev,
                fileName: "",
                fileSize: 0,
            }));
            setError((prev: any) => ({
                ...prev,
                error: [`File size exceeds ${maxSizeMB} MB.`],
                isValid: false,
            }));
            onFileChange(null);
            return;
        }

        onFileChange(file);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] || null;
        e.target.value = "";
        validateAndHandleFile(selectedFile);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (file?.fileName) return;

        const droppedFile = e.dataTransfer.files?.[0];
        validateAndHandleFile(droppedFile || null);
    };

    return (
        <div className="flex flex-col items-start w-full">
            <div className="flex flex-col items-center gap-4 w-full">
                <div
                    className={`flex flex-col items-center justify-center w-full border-2 border-dashed 
                        ${file?.fileName ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        ${isDragging ? 'border-primary-500 bg-darkcolor-600' : 'border-darkcolor-400'} 
                        bg-darkcolor-700 h-[200px] rounded-lg py-5 transition-colors`}
                    onClick={() => {
                        if (!file?.fileName) {
                            fileInputRef.current?.click();
                        }
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        if (!file?.fileName) setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                    }}
                    onDrop={handleDrop}
                >
                    <span className={`text-3xl mb-3 ${file?.fileName ? "opacity-50" : ""}`}>
                        <File size={30} />
                    </span>
                    <button
                        type="button"
                        className={`text-white rounded transition ${file?.fileName ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        onClick={() => { }}
                        disabled={!!file?.fileName}
                    >
                        Select an excel file to upload
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onClick={(e) => {
                            (e.target as HTMLInputElement).value = "";
                        }}
                        onChange={handleInputChange}
                        disabled={!!file?.fileName}
                        accept={accept}
                    />
                    <span className="text-neutral-200">or drag and drop it here</span>
                </div>

                {!file?.fileName && (
                    <div className="flex items-center w-full">
                        <button
                            className="w-full bg-primary-500 text-white px-[14px] py-[10px] rounded-lg"
                            onClick={onClickDownload}
                        >
                            Download template
                        </button>
                    </div>
                )}
            </div>

            {file?.fileName && (
                <div className="w-full mt-2">
                    <div className="border border-darkcolor-400 rounded-lg p-4 bg-darkcolor-800">
                        <div className="flex items-start justify-between mb-1">
                            <span className="text-sm flex flex-wrap text-neutral-25 font-semibold">
                                Uploading file: {file?.fileName}
                            </span>
                            <div className="flex items-center gap-2">
                                {file?.isUploading && (
                                    <span className="animate-spin">
                                        <InProgress size={20} color="#f79009" />
                                    </span>
                                )}
                                {file?.isSuccess && error?.isValid && !file?.isUploading && (
                                    <span className="text-green-500 border-[1px] border-success-600 bg-success-500 w-[20px] h-[20px] flex items-center justify-center rounded-[20px]">
                                        <Tick size={16} color="#FCFCFD" />
                                    </span>
                                )}
                                {!error?.isValid && !file?.isSuccess && !file?.isUploading && (
                                    <span className="text-red-500">
                                        <Error size={20} color="#f04438" />
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-start gap-1">
                            <span className="text-xs text-neutral-25">
                                {(file?.fileSize / 1024).toFixed(2)} KB --
                            </span>
                            <span className="text-xs text-neutral-25">
                                {file?.uploadProgress}% uploaded
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {!error?.isValid && (
                <div className="mt-2 w-full flex flex-col items-center">
                    {error?.error?.length > 0 && error?.error.map((errMsg: string, index: number) => (
                        <span key={index} className="text-[14px] font-bold text-error-500 text-center">
                            {errMsg}
                        </span>
                    ))}
                </div>
            )}

            {file?.isSuccess && error?.isValid && file?.fileName && !file?.isUploading && (
                <div className="flex flex-col items-center w-full mt-2">
                    <span className="text-[14px] font-bold text-neutral-25 mb-2">
                        Records have been uploaded. This bulk update shall impact {file?.impactedRows} rows please confirm to continue.
                    </span>
                    <button
                        type="button"
                        className="px-4 py-2 w-full bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
                        onClick={onClickConfirm}
                    >
                        Confirm
                    </button>
                </div>
            )}
        </div>
    );
};

export default FileUpload;
